import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ResponderUnitInfo from "./ResponderUnitInfo";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../lib/firebase";

function slugToUnitId(slug = "") {
  return String(slug || "").replace(/-/g, "");
}

function slugToUnitLabel(slug = "") {
  return String(slug || "")
    .split("-")
    .map((part) => {
      if (!part) return part;
      if (!Number.isNaN(Number(part))) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
}

function unitIdToLabel(unitId = "") {
  const raw = String(unitId || "").trim();
  if (!raw) return "";

  const match = raw.match(/^([a-zA-Z]+)(\d+)$/);
  if (!match) return raw;

  const [, prefix, num] = match;

  const map = {
  cart: "Cart",
  tent: "Tent",
  bike: "Bike",
  supervisor: "Supervisor",
  transport: "Transport",
  command: "Command",
};

  return `${map[prefix.toLowerCase()] || prefix} ${num}`;
}

function formatTimestamp(value) {
  if (!value) return "—";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";

    return d.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function isActiveCallStatus(status) {
  return [
    "new",
    "queued",
    "requested",
    "dispatched",
    "responding",
    "onScene",
    "transporting",
    "atDestination",
    "arrivedHQ",
    "arrivedHq",
    "arrived_hq",
  ].includes(status);
}

function openReportPopup(path, windowName) {
  const width = 980;
  const height = 900;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
    "resizable=yes",
    "scrollbars=yes",
    "toolbar=no",
    "menubar=no",
    "location=no",
    "status=no",
  ].join(",");

  const win = window.open(path, windowName, features);

  if (!win) {
    alert("Popup blocked. Please allow popups for this site.");
    return null;
  }

  try {
    win.opener = window;
  } catch {
    // ignore
  }

  try {
    win.focus();
  } catch {
    // ignore
  }

  return win;
}

export default function ResponderPage() {
  const navigate = useNavigate();
  const { unitId } = useParams();

  const unitDocId = useMemo(() => slugToUnitId(unitId), [unitId]);
  const fallbackUnitLabel = useMemo(() => slugToUnitLabel(unitId), [unitId]);

  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unitStatus, setUnitStatus] = useState("AVAIL");
  const [cotakBlocked, setCotakBlocked] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  const cotakWindowRef = useRef(null);
  const cotakOpenedRef = useRef(false);

  const incidentReportWindowRef = useRef(null);
  const pcrWindowRef = useRef(null);
  

  const session = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("fieldAccessSession") || "{}");
    } catch {
      return {};
    }
  }, []);

  const unitLabel = session?.unitName || fallbackUnitLabel;

  function openCotakWindow() {
    try {
      const width = Math.floor(window.screen.availWidth * 0.75);
      const height = window.screen.availHeight;
      const left =
        window.screen.availLeft + (window.screen.availWidth - width);
      const top = window.screen.availTop;

      const win = window.open(
        "https://cotak.gov/pages/guest-home",
        "cotakWindow",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      if (!win) {
        setCotakBlocked(true);
        return false;
      }

      cotakWindowRef.current = win;
      setCotakBlocked(false);

      try {
        win.focus();
      } catch {
        // ignore
      }

      return true;
    } catch (error) {
      console.error("Failed to open COTAK window:", error);
      setCotakBlocked(true);
      return false;
    }
  }

  useEffect(() => {
    const sessionAccessType = session?.accessType;
    const sessionUnitSlug = session?.unitSlug;

    if (sessionAccessType !== "responder" || sessionUnitSlug !== unitId) {
      navigate("/field-access/responder", { replace: true });
      return;
    }

    const unitUnsubscribe = onSnapshot(
  doc(db, "dispatchUnits", unitDocId),
  (snap) => {
    if (snap.exists()) {
      setUnitStatus(snap.data()?.status || "AVAIL");
    }
  }
);

    const callsRef = collection(db, "dispatchCalls");
    const q = query(callsRef, orderBy("createdAt", "desc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const calls = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

    const isTransportUnit = unitDocId.startsWith("transport");

function getPossibleAssignedIds(c) {
  const assigned = Array.isArray(c.assignedUnitIds)
    ? c.assignedUnitIds
    : [];

  return [
    ...assigned,
    c.unitId,
    c.assignedUnitId,
    c.transportUnitId,
    c.assignedTransportUnitId,
    c.courtesyTransportUnitId,
    c.respondingUnitId,
  ].filter(Boolean);
}

const activeCall =
  calls.find((c) => {
    const possibleAssignedIds = getPossibleAssignedIds(c);

    return (
      isTransportUnit &&
      c.callType === "courtesyTransport" &&
      possibleAssignedIds.includes(unitDocId) &&
      isActiveCallStatus(c.status)
    );
  }) ||
  calls.find((c) => {
    const possibleAssignedIds = getPossibleAssignedIds(c);

    return (
      (!isTransportUnit || c.callType === "courtesyTransport") &&
      possibleAssignedIds.includes(unitDocId) &&
      isActiveCallStatus(c.status)
    );
  }) ||
  null;

      setCall(activeCall);
      setLoading(false);
    });

    return () => {
  unsubscribe();
  unitUnsubscribe();
};
  }, [navigate, session, unitDocId, unitId]);

  useEffect(() => {
    function handleMessage(event) {
      if (event.origin !== window.location.origin) return;

      const data = event.data || {};
      if (!data || typeof data !== "object") return;

      if (data.type === "incident-report:submitted") {
        try {
          window.focus();
        } catch {
          // ignore
        }

        if (
          incidentReportWindowRef.current &&
          !incidentReportWindowRef.current.closed
        ) {
          try {
            incidentReportWindowRef.current.close();
          } catch {
            // ignore
          }
        }
      }

      if (data.type === "pcr:submitted") {
        try {
          window.focus();
        } catch {
          // ignore
        }

        if (pcrWindowRef.current && !pcrWindowRef.current.closed) {
          try {
            pcrWindowRef.current.close();
          } catch {
            // ignore
          }
        }
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const otherAssignedUnits = useMemo(() => {
    if (!call) return [];

    const assigned = Array.isArray(call.assignedUnitIds)
      ? call.assignedUnitIds
      : [];

    return assigned
      .filter((id) => id !== unitDocId)
      .map((id) => unitIdToLabel(id));
  }, [call, unitDocId]);

  function buildSharedPayload() {
    return {
      unitId: unitDocId,
      unitLabel,
      unitSlug: session?.unitSlug || unitId,
      patientId: session?.patientId || "",
      encounterId: session?.encounterId || "",
      call,
      timestamp: Date.now(),
    };
  }

  function launchIncidentReport() {
    sessionStorage.setItem(
      "activeIncidentReport",
      JSON.stringify(buildSharedPayload())
    );

    incidentReportWindowRef.current = openReportPopup(
      "/reports/incident",
      "IncidentReportWindow"
    );
    setReportsOpen(false);
  }

  function launchPCR() {
    sessionStorage.setItem("activePCR", JSON.stringify(buildSharedPayload()));

    pcrWindowRef.current = openReportPopup("/reports/pcr", "PCRWindow");
    setReportsOpen(false);
  }

  const pageStyle = {
    height: "100dvh",
    background: "#f8fafc",
    padding: 8,
    overflow: "hidden",
  };

  const layoutStyle = {
    display: "grid",
    gridTemplateColumns: "24% 76%",
    gap: 8,
    height: "100%",
  };

  const leftStyle = {
    display: "grid",
    gridTemplateRows: "1fr auto",
    gap: 8,
  };

  const card = {
    background: "#fff",
    borderRadius: 14,
    padding: 14,
    border: "1px solid #e2e8f0",
    overflowY: "auto",
  };

  const btn = {
    width: "100%",
    minHeight: 40,
    borderRadius: 10,
    border: "none",
    background: "#0f766e",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  };

  return (
    <>
    <>
  <ResponderUnitInfo
    unitName={unitLabel}
    unitStatus={unitStatus}
    activeCall={call}
    onOpenInventory={() => {
  navigate(`/responder/${unitId}/inventory`);
}}
onOpenSupplyRequest={() => {
  navigate(`/responder/${unitId}/request-supplies`);
}}
onOpenReports={() => {
  setReportsOpen(true);
}}
  onOpenCotak={() => {
  window.location.href = "itak://";
}}  
    onChangeUnit={() => {
      navigate("/field-access");
    }}
  />
</>

      {reportsOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => setReportsOpen(false)}
        >
          <div
            style={{ background: "#fff", padding: 20, borderRadius: 12, width: 300, display: "grid", gap: 10 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0 }}>Reports</h3>

            <button style={btn} onClick={launchIncidentReport}>
              Incident Report
            </button>

            <button style={btn} onClick={launchPCR}>
              PCR
            </button>

            <button
              style={{ ...btn, background: "#eee", color: "#000" }}
              onClick={() => setReportsOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}